import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { AgremoOrgUsage } from '../response-models/agremo-org-usage.model';
import { AgremoTypeUsage } from '../response-models/agremo-type-usage.model';
import { AgremoUsageStats } from '../response-models/agremo-usage-stats.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableNumber, nullableString } from '../utils/wire-value-utils';

/** Agremo credit usage statistics: GET /admin/agremo/usage (hub `admin-agremo-usage-controller.ts`). */
@Injectable({ providedIn: 'root' })
export class AgremoUsageService extends ApiService {
  constructor() {
    super('admin/agremo/usage');
  }

  getUsageStats(): Observable<AgremoUsageStats> {
    return this.request('GET', {}, toUsageStats);
  }
}

function toUsageStats(data: unknown): AgremoUsageStats {
  const source = isRecord(data) ? data : {};
  return {
    totalConsumed: countOrZero(source['totalConsumed']),
    totalAvailable: countOrZero(source['totalAvailable']),
    totalAcresAnalyzed: nullableNumber(source['totalAcresAnalyzed']) ?? undefined,
    byOrganization: records(source['byOrganization']).map(toOrgUsage),
    byAnalysisType: records(source['byAnalysisType']).map(toTypeUsage),
  };
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function toOrgUsage(row: Record<string, unknown>): AgremoOrgUsage {
  return {
    organizationId: nullableString(row['organizationId']) ?? '',
    organizationName: nullableString(row['organizationName']) ?? '',
    creditsConsumed: countOrZero(row['creditsConsumed']),
    acresAnalyzed: nullableNumber(row['acresAnalyzed']) ?? undefined,
    analysisCount: countOrZero(row['analysisCount']),
  };
}

function toTypeUsage(row: Record<string, unknown>): AgremoTypeUsage {
  return {
    analysisType: nullableString(row['analysisType']) ?? '',
    analysisTypeName: nullableString(row['analysisTypeName']),
    creditsConsumed: countOrZero(row['creditsConsumed']),
    acresAnalyzed: nullableNumber(row['acresAnalyzed']) ?? undefined,
    analysisCount: countOrZero(row['analysisCount']),
  };
}
